import React from "react";
import { useCallback, useState } from "react";
import { useDetectClickOutside } from "react-detect-click-outside";
import { FormattedMessage } from "react-intl";

import {
    ArrowIconWrapper,
    Container,
    List,
    ListContainer,
    PopUp,
    Toggle,
} from "@/components/filters/filters.styles";
import { Checkbox } from "@/components/filters/multi-select/components/checkbox/checkbox";
import { CheckboxContainer } from "@/components/filters/multi-select/components/checkbox/checkbox.styles";
import {
    SelectedCountSpan,
    MultiSelectPopUpListItem,
    TextSpan,
} from "@/components/filters/multi-select/multi-select.styles";
import { ChevronDownSquareCornersIcon24 } from "@/components/icons";

import { DropdownFilterVariant } from "@/types/filters";

import { RoleOptions, StatusOptions } from "@/enums/user";

import { Divider, GroupName, RoleContainer, StatusContainer } from "./users-filter.styles";

export type Option = {
    id: string;
    label: string;
    value?: string | null;
};

type Props = {
    selectedRoles: Option[];
    selectedStatus: Option[];
    onRoleChange: (selected: Option[]) => void;
    onStatusChange: (status: Option[]) => void;
    style?: React.CSSProperties;
    title: string;
    icon: React.ReactElement;
    showSelectAll?: boolean;
    variant: DropdownFilterVariant;
};

export const UserMultiSelectDropdown = ({
    selectedRoles,
    selectedStatus,
    onRoleChange,
    onStatusChange,
    title,
    icon,
    variant = DropdownFilterVariant.Primary,
    ...props
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);

    const filterCounter = selectedRoles.length + selectedStatus.length;

    const handleToggleList = useCallback(() => {
        setIsOpen((currentIsOpen) => !currentIsOpen);
    }, []);

    const handleRoleOptionToggle = useCallback(
        (option: Option) => {
            const isSelected = selectedRoles.some(({ id }) => id === option.id);

            const newSelected = isSelected
                ? selectedRoles.filter(({ id }) => id !== option.id)
                : [...selectedRoles, option];

            onRoleChange(newSelected);
        },
        [onRoleChange, selectedRoles],
    );

    const handleStatusOptionToggle = useCallback(
        (option: Option) => {
            const isSelected = selectedStatus.some(({ id }) => id === option.id);

            const newSelected = isSelected
                ? selectedStatus.filter(({ id }) => id !== option.id)
                : [...selectedStatus, option];

            onStatusChange(newSelected);
        },
        [onStatusChange, selectedStatus],
    );

    const clickOutsideRef = useDetectClickOutside({
        onTriggered: () => {
            setIsOpen(false);
        },
    });

    return (
        <Container ref={clickOutsideRef} {...props}>
            <Toggle isOpen={isOpen} onClick={handleToggleList} variant={variant}>
                {icon}
                <span>{title}</span>
                <SelectedCountSpan>{filterCounter}</SelectedCountSpan>
                <ArrowIconWrapper isOpen={isOpen}>
                    <ChevronDownSquareCornersIcon24 />
                </ArrowIconWrapper>
            </Toggle>

            {isOpen && (
                <PopUp variant={variant} popUpWidth="16.125rem">
                    <ListContainer>
                        <RoleContainer>
                            <GroupName>
                                <FormattedMessage id="users:filter:role" />
                            </GroupName>
                            <Divider />
                            <List>
                                {RoleOptions.map((option: Option) => {
                                    const isChecked = selectedRoles.some(
                                        ({ id }) => id === option.id,
                                    );

                                    return (
                                        <MultiSelectPopUpListItem
                                            key={option.id}
                                            variant={variant}
                                            isChecked={isChecked}
                                            onClick={() => handleRoleOptionToggle(option)}
                                        >
                                            <CheckboxContainer>
                                                <Checkbox
                                                    checked={isChecked}
                                                    onChange={() => handleRoleOptionToggle(option)}
                                                />
                                            </CheckboxContainer>
                                            <TextSpan
                                                className={
                                                    selectedRoles.some(({ id }) => id === option.id)
                                                        ? "checked"
                                                        : ""
                                                }
                                            >
                                                {option.label}
                                            </TextSpan>
                                        </MultiSelectPopUpListItem>
                                    );
                                })}
                            </List>
                        </RoleContainer>

                        <StatusContainer>
                            <GroupName>
                                <FormattedMessage id="users:filter:status" />
                            </GroupName>
                            <Divider />

                            <List data-test="List">
                                {StatusOptions.map((option: Option) => {
                                    const isChecked = selectedStatus.some(
                                        ({ id }) => id === option.id,
                                    );

                                    return (
                                        <MultiSelectPopUpListItem
                                            key={option.id}
                                            variant={variant}
                                            isChecked={isChecked}
                                            onClick={() => {
                                                handleStatusOptionToggle(option);
                                            }}
                                        >
                                            <CheckboxContainer>
                                                <Checkbox
                                                    checked={isChecked}
                                                    onChange={() =>
                                                        handleStatusOptionToggle(option)
                                                    }
                                                />
                                            </CheckboxContainer>
                                            <TextSpan
                                                className={
                                                    selectedStatus.some(
                                                        ({ id }) => id === option.id,
                                                    )
                                                        ? "checked"
                                                        : ""
                                                }
                                            >
                                                {option.label}
                                            </TextSpan>
                                        </MultiSelectPopUpListItem>
                                    );
                                })}
                            </List>
                        </StatusContainer>
                    </ListContainer>
                </PopUp>
            )}
        </Container>
    );
};
